import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as scenesApi from '../api/scenes';

export const useScenes = (gameId) => {
  return useQuery({
    queryKey: ['scenes', gameId],
    queryFn: () => scenesApi.getScenes(gameId),
    enabled: !!gameId,
  });
};

export const useScene = (gameId, sceneId) => {
  return useQuery({
    queryKey: ['scene', gameId, sceneId],
    queryFn: () => scenesApi.getScene(gameId, sceneId),
    enabled: !!gameId && !!sceneId,
  });
};

export const useCreateScene = (gameId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sceneData) => scenesApi.createScene(gameId, sceneData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['game', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};

export const useUpdateScene = (gameId, sceneId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      // Support both pre-bound sceneId and dynamic sceneId in data
      const effectiveSceneId = data.sceneId || sceneId;
      const { sceneId: _, ...sceneData } = data;
      return scenesApi.updateScene(gameId, effectiveSceneId, sceneData);
    },
    onSuccess: (_, variables) => {
      const effectiveSceneId = variables.sceneId || sceneId;
      queryClient.invalidateQueries({ queryKey: ['scene', gameId, effectiveSceneId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};

export const useAddSceneAspect = (gameId, sceneId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (aspectData) => scenesApi.addSceneAspect(gameId, sceneId, aspectData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scene', gameId, sceneId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};

export const useRemoveSceneAspect = (gameId, sceneId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (aspectIndex) => scenesApi.removeSceneAspect(gameId, sceneId, aspectIndex),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scene', gameId, sceneId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['gameState', gameId] });
    },
  });
};

export const useDeleteScene = (gameId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sceneId) => scenesApi.deleteScene(gameId, sceneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
      queryClient.invalidateQueries({ queryKey: ['game', gameId] });
    },
  });
};

export const useAddCharacterToScene = (gameId, sceneId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (characterId) => scenesApi.addCharacterToScene(gameId, sceneId, characterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scene', gameId, sceneId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
    },
  });
};

export const useRemoveCharacterFromScene = (gameId, sceneId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (characterId) => scenesApi.removeCharacterFromScene(gameId, sceneId, characterId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scene', gameId, sceneId] });
      queryClient.invalidateQueries({ queryKey: ['scenes', gameId] });
    },
  });
};
